import React, {useMemo, useState} from 'react'
import type {Collection, ProductCountableEdge} from 'src/api/saleor/generated'
import {Col, Row, media} from 'styled-bootstrap-grid'
import {ProductCard} from 'components/products'
import styled from 'styled-components'
import {theme as Theme} from 'src/themes'
import {palette} from 'styled-tools'
import {SpacingWrapper} from '../layout'
import {H1} from '../typography'
import {Checkbox} from '../form'
import {Loader} from '../common'

interface Props {
  collection: Collection
  checked: boolean
  onClick: () => void
  sortClass: string
  handleChange: (filter: Filters) => void
  showLoading: boolean
}

type Filters = 'new' | 'price_asc' | 'alph_asc'

const GetItem = ({
  product,
}: {
  product: ProductCountableEdge
}): JSX.Element | null => {
  const element = useMemo(
    () => (
      <ProductCard
        key={product.node.id}
        slug={product.node.slug}
        render={(children) => (
          <Col key={product.node?.id} col={12} sm={6} md={4} lg={3}>
            {children}
          </Col>
        )}
      />
    ),
    [product],
  )
  if (
    !element ||
    !product.node.id ||
    !product.node.variants ||
    product.node.variants.length === 0
  ) {
    return null
  }
  return element
}

const LoaderContainer = styled.div`
  width: 100%;
  display: grid;
  place-items: center;
`
const CenterContainer = styled.div`
  position: relative;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 20px;
  over-flow: visible;
`
const SortDropdown = styled.div`
position: relative;
top:0;
right:0;
width: 220px;
display: flex;
flex-direction: column;
justify-content: center;
align-items: center;
background-color: ${palette('white')};
border-radius: 5px;
border: 1.5px solid ${palette('black')};
  .sort-button {
    background-color: transparent;
    border: none !important;
    outline: none !important;
    width: 100%;
    height: 40px;
    display: flex;
    flex-direction: row;
    align-items: center;
    span {
      font-size: 16px;
      flex-grow: 1;
      display: flex;
      justify-content: flex-start;
    }
  }
}
.options {
  width: 100%;
  position: absolute;
  top: 40px;
  z-index: 10;
  border: 1px solid ${palette('black')};
  background-color: ${palette('white')};
  ${media.xl`
    position: relative;
    top: 0;
    border-top: border-top: 1px solid ${palette('black')};
    border-bottom: none !important;
    border-left: none !important;
    border-right: none;
  `}
  li {
    padding: 5px;
    button {
    width: 100%;
    text-align: left;
    background-color: transparent;
    border: none !important;
    font-size: 16px;
    &:hover {
      color: ${Theme.palette.error};
    }
    .selected {
      button {
        color: ${Theme.palette.error};
      }
    }
    }
    &:hover {
      cursor: pointer;
    }
  }
}
align-self: flex-end;
margin-bottom: 10px;
.arrow {
  border: solid black;
  border-width: 0 3px 3px 0;
  display: inline-block;
  padding: 3px;
}
.up {
  transform: rotate(-135deg);
  -webkit-transform: rotate(-135deg);
}
.down {
  transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
}
`
export default function CollectionPage({
  collection,
  checked,
  onClick,
  sortClass,
  handleChange,
  showLoading,
}: Props): JSX.Element {
  const SortContainer = (): JSX.Element => {
    const [showDropdown, setShowDropdown] = useState<boolean>(false)
    const options: Record<Filters, string> = {
      new: 'Nýjast',
      price_asc: 'Lægsta verð',
      alph_asc: 'Nafn',
    }
    return (
      <SortDropdown>
        <button
          type="button"
          className="sort-button"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <span>
            {sortClass
              ? `Raða eftir: ${options[sortClass as Filters]}`
              : 'Raða eftir: Nafn'}
          </span>
          <i className={showDropdown ? 'arrow up' : 'arrow down'} />
        </button>
        {showDropdown && (
          <div className="options">
            <ul>
              <li>
                <button
                  type="button"
                  // onClick={() => handleChange({ field: "DATE", direction: "DESC" }, "new")}
                  onClick={() => handleChange('new')}
                  className={sortClass === 'new' ? 'selected' : ''}
                >
                  {options.new}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleChange('price_asc')}
                  className={sortClass === 'price_asc' ? 'selected' : ''}
                >
                  {options.price_asc}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleChange('alph_asc')}
                  className={sortClass === 'alph_asc' ? 'selected' : ''}
                >
                  {options.alph_asc}
                </button>
              </li>
            </ul>
          </div>
        )}
      </SortDropdown>
    )
  }
  return (
    <SpacingWrapper spacing={32}>
      <H1 withGutter>{collection?.name || 'Vefverslun'}</H1>
      <CenterContainer>
        <Checkbox
          label="Sía vistvænar vörur"
          checked={checked}
          onClick={onClick}
        />
        <SortContainer />
      </CenterContainer>
      {showLoading && (
        <LoaderContainer>
          <Loader loaderKey={collection?.name} size={48} />
        </LoaderContainer>
      )}
      {!showLoading && (
        <Row>
          {collection?.products?.edges?.map((product, index) => (
            <GetItem product={product} key={product.node.slug ?? index} />
          ))}
        </Row>
      )}
    </SpacingWrapper>
  )
}
